import React, { useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { DocumentTemplateSystem } from 'src/types/DocumentSettings';
import {
  authoredWordCount,
  countBlocksUsed,
  countHeartWordsUsed,
  countImportantWordsUsed,
  countSignalWordsUsed,
  countPastedWords,
  countWriteTimeMs,
  formatWriteTime,
  parseMeta,
  HEAVY_PASTE_WORD_THRESHOLD,
} from 'src/utils/assignmentMetrics';
import { isDemoSeed, getSeededShowWork } from 'src/utils/demoSeed';
import { Description } from '../typography';
import { PersonImg } from '../../assets/icons';
import { AssignmentDocumentButtonProps } from './types';
import StatusTag from '../StatusTag';
import { ButtonContainer } from './styles';

export default function AssignmentDocumentButton({
  pressto: document = {},
  user,
  status = 'new',
  isExpanded: controlledExpanded,
  onToggleExpanded,
}: AssignmentDocumentButtonProps) {
  const {
    id,
    template_system: templateSystem,
  } = document;

  const navigate = useNavigate();
  const location = useLocation();

  // When a parent provides isExpanded/onToggleExpanded the toggle is shared
  // across all cards (the Assignment review grid does this so one chevron
  // click expands every student). Falls back to per-card local state.
  const [localExpanded, setLocalExpanded] = useState(false);
  const isExpanded = controlledExpanded ?? localExpanded;
  const handleToggle = onToggleExpanded ?? (() => setLocalExpanded((v) => !v));

  const [showNotStartedHint, setShowNotStartedHint] = useState(false);

  const isStarted = status !== 'new';
  const meta = useMemo(() => parseMeta(document.meta), [document.meta]);
  const isBlocksDoc = templateSystem === DocumentTemplateSystem.BLOCKS;

  const wordCount = useMemo(
    () => authoredWordCount({
      ...document,
      version: document.version
        ? {
          ...document.version,
          pages: document.version.content?.pages || document.version.pages,
        }
        : undefined,
    }),
    [document],
  );

  const blocks = useMemo(
    () => (isBlocksDoc ? countBlocksUsed(document, isStarted) : { used: 0, total: 0 }),
    [document, isStarted, isBlocksDoc],
  );
  const heart = useMemo(
    () => countHeartWordsUsed(document, meta.heartWords, isStarted),
    [document, meta.heartWords, isStarted],
  );
  const important = useMemo(
    () => countImportantWordsUsed(document, meta.importantWords, isStarted),
    [document, meta.importantWords, isStarted],
  );
  const signal = useMemo(
    () => countSignalWordsUsed(document, meta.writingPlan, isStarted),
    [document, meta.writingPlan, isStarted],
  );

  // Show Work signals: paste count + active writing time. We display these
  // in the expanded panel only for students who've started — they don't
  // exist on the base-document copy that a Not-Started student inherits.
  // When `?demo=seed` is in the URL, swap in hardcoded plausible values so the
  // dashboard demos cleanly without needing real student writing data.
  const seedOn = isDemoSeed(location.search);
  const studentName = `${user?.name || ''} ${user?.surname || ''}`.trim();
  const pastedWords = useMemo(
    () => {
      if (seedOn) return getSeededShowWork(studentName, isStarted).pastedWords;
      return isStarted ? countPastedWords(document) : 0;
    },
    [document, isStarted, seedOn, studentName],
  );
  const writeTimeMs = useMemo(
    () => {
      if (seedOn) return getSeededShowWork(studentName, isStarted).writeTimeMs;
      return isStarted ? countWriteTimeMs(document) : 0;
    },
    [document, isStarted, seedOn, studentName],
  );
  const isHeavyPaste = pastedWords > HEAVY_PASTE_WORD_THRESHOLD;

  const showHeart = Boolean(meta.hasHeartWords && heart.total > 0);
  const showImportant = Boolean(meta.hasImportantWords && important.total > 0);
  const showSignal = Boolean(meta.hasSignalWords && signal.total > 0);
  const showBlocks = isBlocksDoc && blocks.total > 0;
  // Always show the Show Work rows on started cards even if other metrics
  // are off — they're per-student signals, not assignment-config metrics.
  const showPace = isStarted;
  const hasAnyMetric = showBlocks || showHeart || showImportant || showSignal || showPace;

  // Progress = block completion. Other metrics are quality, not progress.
  const progressPct = blocks.total > 0
    ? Math.round((blocks.used / blocks.total) * 100)
    : 0;

  const handleCardClick = (e: React.MouseEvent) => {
    // Don't fire when clicking the expand toggle.
    if ((e.target as HTMLElement).closest('.expand-toggle')) return;

    if (!isStarted) {
      setShowNotStartedHint(true);
      window.setTimeout(() => setShowNotStartedHint(false), 1600);
      return;
    }
    navigate(`/document/${id}`, { state: { prev: location.pathname } });
  };

  return (
    <ButtonContainer
      className={`pressto-button${isHeavyPaste ? ' heavy-paste' : ''}`}
      onClick={handleCardClick}
      elevation={4}
    >
      <div className="card-header">
        <img
          src={user?.avatar ?? PersonImg}
          alt={`${user.name} ${user.surname}`}
          className="avatar"
        />
        <Description className="student-name">
          {`${user.name || ''} ${user.surname || ''}`.trim()}
        </Description>
        {hasAnyMetric && (
          <button
            type="button"
            className={`expand-toggle${isExpanded ? ' open' : ''}`}
            aria-label={isExpanded ? 'Hide details' : 'Show details'}
            onClick={(e) => {
              e.stopPropagation();
              handleToggle();
            }}
          >
            <svg viewBox="0 0 12 12" width="12" height="12" aria-hidden="true">
              <path
                d="M3 4.5L6 7.5L9 4.5"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.75"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
        )}
      </div>

      <div className="status-row">
        <StatusTag status={status} isTeacher />
      </div>

      <div className={`progress-block${isStarted ? '' : ' not-started'}`}>
        <div className="progress-line">
          <span className="word-count-pill">
            <strong>{wordCount}</strong>
            <span className="word-count-label">words</span>
          </span>
          {showBlocks && (
            <span className="blocks-pill">
              {blocks.used}<span className="metric-sep">/</span>{blocks.total} blocks
            </span>
          )}
        </div>
        {showBlocks && (
          <div className="progress-bar" aria-label={`${progressPct}% complete`}>
            <div
              className="progress-bar-fill"
              style={{ width: `${progressPct}%` }}
            />
          </div>
        )}
      </div>

      {isExpanded && hasAnyMetric && (
        <div className={`metric-details${isStarted ? '' : ' not-started'}`}>
          {showHeart && (
            <div className="metric-row heart">
              <span className="metric-name">
                <span className="metric-icon" aria-hidden="true">♥</span>
                Heart
              </span>
              <span className="metric-value">
                <strong>{heart.used}</strong>
                <span className="metric-sep">/</span>
                {heart.total}
              </span>
            </div>
          )}
          {showImportant && (
            <div className="metric-row important">
              <span className="metric-name">
                <span className="metric-icon" aria-hidden="true">★</span>
                Important
              </span>
              <span className="metric-value">
                <strong>{important.used}</strong>
                <span className="metric-sep">/</span>
                {important.total}
              </span>
            </div>
          )}
          {showSignal && (
            <div className="metric-row signal">
              <span className="metric-name">
                <span className="metric-icon" aria-hidden="true">»</span>
                Signal
              </span>
              <span className="metric-value">
                <strong>{signal.used}</strong>
                <span className="metric-sep">/</span>
                {signal.total}
              </span>
            </div>
          )}
          {showPace && (
            <div className="metric-row pace">
              <span className="metric-name">
                <span className="metric-icon" aria-hidden="true">✏️</span>
                Wrote in
              </span>
              <span className="metric-value">
                <strong>{formatWriteTime(writeTimeMs)}</strong>
              </span>
            </div>
          )}
          {showPace && (
            <div className={`metric-row paste${isHeavyPaste ? ' heavy' : ''}`}>
              <span className="metric-name">
                <span className="metric-icon" aria-hidden="true">📋</span>
                Pasted
              </span>
              <span className="metric-value">
                <strong>{pastedWords}</strong>
                <span className="paste-unit">{pastedWords === 1 ? ' word' : ' words'}</span>
              </span>
            </div>
          )}
        </div>
      )}

      {showNotStartedHint && (
        <div className="not-started-hint" role="status">
          Not yet started
        </div>
      )}
    </ButtonContainer>
  );
}
